/**
 * Possible extra revenue per year from more organic visits, years 1–5.
 *
 * Traffic grows gradually: the yearly growth is spread evenly over the months
 * (compounded monthly), so year 1 already includes part of the increase.
 * Extra revenue is measured against unchanged traffic with the same conversion
 * and deal value. For "contact and quote" both the enquiry rate and the share
 * of enquiries that become customers apply. First-deal value only, no lifetime
 * value.
 */

export type SalesModel = "online" | "lead";

export type RevenueField = "visits" | "value" | "growth" | "conversion" | "close";

export const REVENUE_PROJECTION_YEARS = 5;

export type RevenueYearSeries = [
  number,
  number,
  number,
  number,
  number,
];

export type RevenueInputs = {
  visitsRaw: string;
  valueRaw: string;
  model: SalesModel;
  growthRaw: string;
  conversionRaw: string;
  closeRaw: string;
};

/** Summary metrics derived from a completed projection (honest to the model). */
export type RevenueProjectionKpis = {
  /** Sum of extra revenue in years 1–5 (SEK, excl. VAT). */
  cumulativeRevenue5y: number;
  /** Extra customers in year 5 from incremental visits × conversion × close. */
  extraCustomersYear5: number;
  /** Year-5 extra revenue ÷ 12 (average month within that year). */
  monthlyExtraRevenueYear5: number;
  /** Cumulative 5y revenue ÷ deal value — “as many deals as” over the period. */
  equivalentDeals5y: number;
  /** Steady-state annual revenue from current monthly visits (12 × visits × revenue/visit). */
  baselineAnnualRevenue: number;
  /** Baseline annual revenue plus year-5 extra revenue from incremental visits. */
  projectedAnnualRevenueYear5: number;
  /** Year-5 extra revenue as % of baseline; 0 when baseline is 0. */
  extraRevenuePercentYear5: number;
};

export type RevenueProjection =
  | { kind: "incomplete"; empty: RevenueField[]; invalid: RevenueField[] }
  | {
      kind: "value";
      visits: number;
      /** Extra revenue in each projection year (SEK, excl. VAT). */
      years: RevenueYearSeries;
      /** Extra visits in each year, for the "how we calculate" text. */
      extraVisits: RevenueYearSeries;
      kpis: RevenueProjectionKpis;
    };

/** Slider ranges for Intäktskalkyl v2 (main fields + assumption percents). */
export type TrafficCalculatorSliderRange = {
  min: number;
  max: number;
  /** Committed increment (typing blur, slider release). */
  step: number;
  /** Finer slider step while dragging; snaps to `step` on release. */
  dragStep?: number;
  fallback: number;
};

export const TRAFFIC_CALCULATOR_SLIDER_RANGES = {
  visits: { min: 0, max: 100_000, step: 100, dragStep: 1, fallback: 1_000 },
  value: { min: 1_000, max: 500_000, step: 1_000, fallback: 30_000 },
  growth: { min: 0, max: 100, step: 1, fallback: 30 },
  conversion: { min: 0, max: 100, step: 0.5, fallback: 3 },
  close: { min: 0, max: 100, step: 1, fallback: 25 },
} as const;

/** Currency shown by the calculators: pounds in English, Danish, Norwegian or Swedish kroner otherwise. */
export function calculatorCurrency(locale: string): "GBP" | "DKK" | "EUR" | "NOK" | "SEK" {
  if (locale.startsWith("en")) return "GBP";
  if (locale.startsWith("da")) return "DKK";
  if (locale.startsWith("de") || locale.startsWith("nl") || locale.startsWith("es") || locale.startsWith("fr") || locale.startsWith("pt") || locale.startsWith("it") || locale.startsWith("fi")) return "EUR";
  if (locale.startsWith("nb") || locale.startsWith("no")) return "NOK";
  return "SEK";
}

/** Rough SEK factors so sliders start at believable local amounts. */
const CURRENCY_SCALE: Partial<Record<ReturnType<typeof calculatorCurrency>, number>> = {
  GBP: 0.1,
  DKK: 0.7,
  EUR: 0.09,
};

/** Rounds a scaled amount to two significant digits (21 700 → 22 000). */
function roundAmount(value: number): number {
  if (value < 10) return Math.max(1, Math.round(value));
  const magnitude = 10 ** (Math.floor(Math.log10(value)) - 1);
  return Math.round(value / magnitude) * magnitude;
}

/**
 * Money slider range for the page currency. Ranges are authored in SEK; for
 * pounds and Danish kroner and euros every bound is scaled and kept a whole number of at
 * least 1, rounded so the sliders show tidy amounts.
 */
export function moneyRange<R extends TrafficCalculatorSliderRange>(
  range: R,
  locale: string,
): TrafficCalculatorSliderRange {
  const factor = CURRENCY_SCALE[calculatorCurrency(locale)];
  if (!factor) return range;
  const scale = factor === CURRENCY_SCALE.GBP
    ? (n: number) => Math.max(1, Math.round(n * factor))
    : (n: number) => roundAmount(n * factor);
  return {
    min: range.min === 0 ? 0 : scale(range.min),
    max: scale(range.max),
    step: scale(range.step),
    ...(range.dragStep ? { dragStep: scale(range.dragStep) } : {}),
    fallback: scale(range.fallback),
  };
}

/** Whole number, Swedish or English thousands separators allowed. null = empty, NaN = invalid. */
export function parseWholeNumber(raw: string): number | null {
  const cleaned = raw.replace(/[\s  .,](?=\d{3}(\D|$))/g, "").replace(/[\s  ]/g, "");
  if (cleaned === "") return null;
  return /^\d+$/.test(cleaned) ? Number(cleaned) : Number.NaN;
}

/** Display string for whole-number calculator fields (matches Swedish UI spacing). */
export function formatWholeNumber(value: number, locale = "sv-SE"): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    Math.round(value),
  );
}

/** Percentage 0–100, decimal comma or point. null = empty, NaN = invalid or out of range. */
export function parsePercent(raw: string): number | null {
  const cleaned = raw.replace(/[\s%]/g, "").replace(",", ".");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return Number.NaN;
  const value = Number(cleaned);
  return value <= 100 ? value : Number.NaN;
}

/**
 * Display string for percent calculator fields: decimal comma when needed,
 * or a decimal point for English locales (pass the page locale, e.g. "en-GB").
 */
export function formatPercentRaw(value: number, locale = "sv-SE"): string {
  const rounded = Math.round(value * 10) / 10;
  if (Number.isInteger(rounded)) return String(rounded);
  return locale.startsWith("en") ? String(rounded) : String(rounded).replace(".", ",");
}

/** Percentage with its sign, following the locale's typography ("30%" in English, "30 %" in Swedish). */
export function formatPercentLabel(value: number, locale = "sv-SE"): string {
  const raw = formatPercentRaw(value, locale);
  return locale.startsWith("en") ? `${raw}%` : `${raw} %`;
}

/** Amount with its unit; a unit that starts with a currency symbol ("£ excl. VAT") puts the symbol first. */
export function formatAmountWithUnit(value: number, unit: string, locale = "sv-SE"): string {
  const amount = formatWholeNumber(value, locale);
  const symbol = unit.match(/^[£$€]/)?.[0];
  return symbol ? `${symbol}${amount}${unit.slice(1)}` : `${amount} ${unit}`;
}

export function snapWholeToSliderStep(
  value: number,
  range: { min: number; max: number; step: number },
): number {
  const step = range.step;
  if (step <= 0) {
    return Math.min(range.max, Math.max(range.min, value));
  }
  const snapped = Math.round(value / step) * step;
  return Math.min(range.max, Math.max(range.min, snapped));
}

export function snapPercentToSliderStep(
  value: number,
  range: { min: number; max: number; step: number },
): number {
  const step = range.step;
  if (step <= 0) {
    return Math.min(range.max, Math.max(range.min, value));
  }
  const snapped = Math.round(value / step) * step;
  const decimals = step < 1 ? String(step).split(".")[1]?.length ?? 1 : 0;
  const rounded =
    decimals > 0 ? Math.round(snapped * 10 ** decimals) / 10 ** decimals : snapped;
  return Math.min(range.max, Math.max(range.min, rounded));
}

/** Fine step while dragging; `step` still applies on blur / slider release. */
const SMOOTH_SLIDER_POSITIONS = 500;

export function resolveSliderDragStep(range: TrafficCalculatorSliderRange): number {
  const span = range.max - range.min;
  if (span <= 0) {
    return range.step > 0 ? range.step : 1;
  }

  const { dragStep, step } = range;
  if (dragStep !== undefined && dragStep < step) {
    return dragStep;
  }

  const fine = span / SMOOTH_SLIDER_POSITIONS;
  if (step >= 1 && Number.isInteger(step)) {
    return Math.max(1, Math.round(fine) || 1);
  }
  return Math.max(0.01, Math.min(step / 10, fine));
}

export function clampWholeForSlider(
  raw: string,
  range: { min: number; max: number; fallback: number },
): number {
  const parsed = parseWholeNumber(raw);
  if (parsed === null || Number.isNaN(parsed)) return range.fallback;
  return Math.min(range.max, Math.max(range.min, parsed));
}

export function clampPercentForSlider(
  raw: string,
  range: { min: number; max: number; fallback: number },
): number {
  const parsed = parsePercent(raw);
  if (parsed === null || Number.isNaN(parsed)) return range.fallback;
  return Math.min(range.max, Math.max(range.min, parsed));
}

/** Sum of extra visits per year when traffic grows gradually month by month. */
export function extraVisitsPerYear(
  visits: number,
  growthPercent: number,
): RevenueYearSeries {
  const years = Array(REVENUE_PROJECTION_YEARS).fill(0) as RevenueYearSeries;
  const monthCount = REVENUE_PROJECTION_YEARS * 12;
  const monthly = (1 + growthPercent / 100) ** (1 / 12);
  for (let month = 1; month <= monthCount; month += 1) {
    years[Math.ceil(month / 12) - 1] += visits * (monthly ** month - 1);
  }
  return years;
}

export function computeRevenueProjectionKpis(
  years: RevenueYearSeries,
  extraVisits: RevenueYearSeries,
  conversionPercent: number,
  closePercent: number,
  dealValue: number,
  monthlyVisits: number,
): RevenueProjectionKpis {
  const cumulativeRevenue5y = years.reduce((sum, year) => sum + year, 0);
  const lastYear = REVENUE_PROJECTION_YEARS - 1;
  const customerRate = (conversionPercent / 100) * (closePercent / 100);
  const revenuePerVisit = customerRate * dealValue;
  const extraCustomersYear5 = extraVisits[lastYear] * customerRate;
  const monthlyExtraRevenueYear5 = years[lastYear] / 12;
  const equivalentDeals5y = dealValue > 0 ? cumulativeRevenue5y / dealValue : 0;
  const baselineAnnualRevenue = 12 * monthlyVisits * revenuePerVisit;
  const year5Extra = years[lastYear];
  const projectedAnnualRevenueYear5 = baselineAnnualRevenue + year5Extra;
  const extraRevenuePercentYear5 =
    baselineAnnualRevenue > 0 ? (year5Extra / baselineAnnualRevenue) * 100 : 0;

  return {
    cumulativeRevenue5y,
    extraCustomersYear5,
    monthlyExtraRevenueYear5,
    equivalentDeals5y,
    baselineAnnualRevenue,
    projectedAnnualRevenueYear5,
    extraRevenuePercentYear5,
  };
}

export function projectRevenue(inputs: RevenueInputs): RevenueProjection {
  const parsed: Record<RevenueField, number | null> = {
    visits: parseWholeNumber(inputs.visitsRaw),
    value: parseWholeNumber(inputs.valueRaw),
    growth: parsePercent(inputs.growthRaw),
    conversion: parsePercent(inputs.conversionRaw),
    close: inputs.model === "lead" ? parsePercent(inputs.closeRaw) : 100,
  };

  const fields = Object.keys(parsed) as RevenueField[];
  const empty = fields.filter((field) => parsed[field] === null);
  const invalid = fields.filter((field) => Number.isNaN(parsed[field]));
  if (empty.length || invalid.length) return { kind: "incomplete", empty, invalid };

  const visits = parsed.visits as number;
  const dealValue = parsed.value as number;
  const extraVisits = extraVisitsPerYear(visits, parsed.growth as number);
  const revenuePerVisit =
    ((parsed.conversion as number) / 100) * ((parsed.close as number) / 100) * dealValue;
  const years = extraVisits.map((extra) => extra * revenuePerVisit) as RevenueYearSeries;

  return {
    kind: "value",
    visits,
    years,
    extraVisits,
    kpis: computeRevenueProjectionKpis(
      years,
      extraVisits,
      parsed.conversion as number,
      parsed.close as number,
      dealValue,
      visits,
    ),
  };
}
